/**
 * Meta Muse Spark contributor-tier RPM capacity gauge
 * (muse_spark_provider_addition_2026_08_25). Mirrors NvidiaCapacityPanel.tsx's
 * shape -- ONE shared gauge, not one row per account, since Meta's contributor-tier
 * RPM ceiling is per-TEAM (shared across every muse account), same pooling model
 * as NVIDIA's per-KEY sharing (see the backend muse_headroom.py module docstring).
 *
 * Unlike NvidiaCapacityPanel, there is no degraded-accounts banner here -- that
 * banner exists for a specific, dated NVIDIA incident (gemma_4_31b_it_persistent_
 * timeout_2026_08_19) with no Muse equivalent.
 */

import { useEffect, useState } from "react";

import { api } from "./api";
import { Panel } from "./components";
import type { MuseCapacityView } from "./types";

const POLL_INTERVAL_MS = 30000;

function usagePct(used: number, ceiling: number): number {
  return ceiling <= 0 ? 0 : Math.min(100, (used / ceiling) * 100);
}

function GaugeBar({ used, ceiling }: { used: number; ceiling: number }) {
  const pct = usagePct(used, ceiling);
  const tone =
    pct >= 90 ? "capacity-gauge-fill-danger" : pct >= 60 ? "capacity-gauge-fill-warn" : "capacity-gauge-fill-ok";
  return (
    <div className="capacity-gauge-track">
      <div className={`capacity-gauge-fill ${tone}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function MuseCapacityPanel({ baseUrl }: { baseUrl: string }) {
  const [data, setData] = useState<MuseCapacityView | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await api.getMuseCapacity(baseUrl);
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    };
    void load();
    const id = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl]);

  return (
    <Panel title="Muse Capacity" collapsible tone="accounts">
      <div className="usage-panel-hint">
        Muse's contributor tier bills per token (see Task Token Usage) but ALSO carries a real request-rate ceiling —
        100 RPM, GLOBAL PER TEAM (dev.meta.ai/docs/pricing-rate-limits): every muse account listed below shares the SAME
        RPM pool, so dispatching them concurrently does not multiply available capacity, it just draws the shared
        ceiling down faster. No live vendor quota-check API exists for Muse, so this is a local selection-count against
        the published ceiling, not a real-time vendor read.
      </div>
      {error && <div className="task-usage-error">Failed to load: {error}</div>}
      {data === undefined && !error && <div className="task-usage-pending">loading…</div>}
      {data === null && !error && <div className="task-usage-pending">no Muse accounts registered</div>}
      {data && !error && (
        <div className="capacity-account">
          <div className="capacity-account-header">
            <span className="mono">{data.account_labels.join(" + ")}</span>
            <span className="capacity-account-meta">
              shared team{data.ceiling_confirmed ? "" : " · ceiling unconfirmed"}
            </span>
          </div>
          <table className="acc-usage-table acc-usage-table-breakdown">
            <tbody>
              <tr>
                <td className="acc-usage-window-label">RPM (shared)</td>
                <td className="mono">
                  {data.rpm_used} / {data.rpm_ceiling}
                </td>
                <td>
                  <GaugeBar used={data.rpm_used} ceiling={data.rpm_ceiling} />
                </td>
              </tr>
            </tbody>
          </table>
          {!data.ceiling_confirmed && (
            <div className="acc-usage-note">
              {data.rpm_ceiling} RPM is Meta's own published contributor-tier figure, not yet empirically confirmed
              against this fleet's own key.
              {data.ceiling_hit_count_24h > 0 ? (
                <>
                  {" "}
                  Our own gate has declined a dispatch for hitting it {data.ceiling_hit_count_24h} time
                  {data.ceiling_hit_count_24h === 1 ? "" : "s"} in the last 24h — check the activity feed for
                  `muse_rpm_ceiling_hit` events alongside any real 429 to calibrate the true figure.
                </>
              ) : (
                " Never actually hit in the last 24h."
              )}
            </div>
          )}
          {data.real_quota_checked_at ? (
            <>
              <table className="acc-usage-table acc-usage-table-breakdown">
                <tbody>
                  {data.real_requests_limit !== null && (
                    <tr>
                      <td className="acc-usage-window-label">Requests (real, live)</td>
                      <td className="mono">
                        {(data.real_requests_limit ?? 0) - (data.real_requests_remaining ?? 0)} /{" "}
                        {data.real_requests_limit}
                      </td>
                      <td>
                        <GaugeBar
                          used={(data.real_requests_limit ?? 0) - (data.real_requests_remaining ?? 0)}
                          ceiling={data.real_requests_limit ?? 0}
                        />
                      </td>
                    </tr>
                  )}
                  {data.real_tokens_limit !== null && (
                    <tr>
                      <td className="acc-usage-window-label">Tokens (real, live)</td>
                      <td className="mono">
                        {(data.real_tokens_limit ?? 0) - (data.real_tokens_remaining ?? 0)} / {data.real_tokens_limit}
                      </td>
                      <td>
                        <GaugeBar
                          used={(data.real_tokens_limit ?? 0) - (data.real_tokens_remaining ?? 0)}
                          ceiling={data.real_tokens_limit ?? 0}
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="acc-usage-note">
                Real vendor read (x-ratelimit-* response headers), last checked {data.real_quota_checked_at} — the
                window duration behind these two numbers is unknown (Meta's response carries no reset-time header), so
                they are NOT a 5-hour/weekly percentage and may not match the published RPM figure above (two real
                numbers from two different sources).
              </div>
            </>
          ) : (
            <div className="acc-usage-note">no real vendor capacity read yet — MuseQuotaPoller has not ticked</div>
          )}
        </div>
      )}
    </Panel>
  );
}
